import type { ReactNode } from 'react'
import { cn } from '@/helpers/utils'

interface InlineBannerProps {
	title: string
	description?: ReactNode
	/** Trailing action, e.g. a button. Full width below the breakpoint. */
	action?: ReactNode
	/** Leading icon shown before the text. */
	icon?: ReactNode
	/**
	 * Where the icon sits against the text block: centred on the whole block
	 * (default), or on the title line, for a 20px icon that qualifies the
	 * title rather than the banner.
	 */
	iconPlacement?: 'center' | 'title'
	className?: string
}

/**
 * A full-width inline banner: a leading icon (optional), a title + description,
 * and a trailing action. Stacks on narrow screens with the action going full
 * width. Used for contextual prompts such as "Ready to lock this event?".
 */
export function InlineBanner({
	title,
	description,
	action,
	icon,
	iconPlacement = 'center',
	className
}: InlineBannerProps) {
	return (
		<div
			className={cn(
				// `rounded-2xl` is 16px on this theme (--radius 10px + 6).
				// No border: the grey-100 fill is what separates the banner
				// from the page, and an outline on top of it reads as a card.
				'flex l:flex-row flex-col l:items-center l:justify-between gap-4 rounded-2xl bg-grey-100 p-6',
				className
			)}
		>
			{/* Centred by default: the icon sits against the whole text
			    block, so it stays put whether the description runs to one
			    line or three. On the title line, the 2px offset centres a
			    20px icon on the title's 24px line height. */}
			<div
				className={cn(
					'flex gap-3',
					iconPlacement === 'title' ? 'items-start' : 'items-center'
				)}
			>
				{icon && (
					<span
						className={cn(
							'shrink-0',
							iconPlacement === 'title' && 'mt-0.5'
						)}
					>
						{icon}
					</span>
				)}
				<div className='flex flex-col gap-1'>
					<p className='font-medium text-base text-grey-1000'>
						{title}
					</p>
					{description && (
						<p className='text-grey-700 text-sm'>{description}</p>
					)}
				</div>
			</div>
			{action && <div className='l:w-auto w-full shrink-0'>{action}</div>}
		</div>
	)
}
